import { Injectable } from '@angular/core';
import { RouteReuseStrategy } from '@angular/router';
import { ConfigurableReuseStrategy, RouteReuseConfig } from './configurable-reuse-strategy';
import { NoReuseStrategy } from './no-reuse-strategy';

/**
 * Type of route reuse strategy to use
 */
export enum RouteReuseStrategyType {
  /** Never reuse routes */
  NONE = 'none',
  
  /** Use the default Angular route reuse strategy */
  DEFAULT = 'default',
  
  /** Use a configurable route reuse strategy */
  CONFIGURABLE = 'configurable'
}

/**
 * Service to manage route reuse strategies
 */
@Injectable({
  providedIn: 'root'
})
export class RouteReuseService {
  /** The current route reuse strategy instance */
  private currentStrategy: RouteReuseStrategy | null = null;
  
  /** The configurable strategy instance (if being used) */
  private configurableStrategy: ConfigurableReuseStrategy | null = null;

  /**
   * Creates a route reuse strategy of the specified type
   * @param type The type of strategy to create
   * @param config Configuration for the configurable strategy (if applicable)
   * @returns A route reuse strategy instance
   */
  public createStrategy(type: RouteReuseStrategyType, config?: Partial<RouteReuseConfig>): RouteReuseStrategy {
    switch (type) {
      case RouteReuseStrategyType.NONE:
        this.currentStrategy = new NoReuseStrategy();
        this.configurableStrategy = null;
        break;
        
      case RouteReuseStrategyType.CONFIGURABLE:
        this.configurableStrategy = new ConfigurableReuseStrategy(config);
        this.currentStrategy = this.configurableStrategy;
        break;
        
      case RouteReuseStrategyType.DEFAULT:
      default:
        // Return null to use Angular's default strategy
        this.currentStrategy = null;
        this.configurableStrategy = null;
        break;
    }
    
    return this.currentStrategy as RouteReuseStrategy;
  }

  /**
   * Updates the configuration of the configurable strategy
   * @param config New configuration to apply
   * @throws Error if the current strategy is not configurable
   */
  public updateConfig(config: Partial<RouteReuseConfig>): void {
    if (!this.configurableStrategy) {
      throw new Error('Cannot update configuration: current strategy is not configurable');
    }
    
    this.configurableStrategy.updateConfig(config);
  }

  /**
   * Clears all stored routes in the configurable strategy
   * Useful when logging out or when you want to force fresh route creation
   * @throws Error if the current strategy is not configurable
   */
  public clearStoredRoutes(): void {
    if (!this.configurableStrategy) {
      throw new Error('Cannot clear stored routes: current strategy is not configurable');
    }
    
    this.configurableStrategy.clearStoredRoutes();
  }
}
